// Pure helpers for clip views / reactions / comments (no Supabase import, so
// they're unit-testable). Data access lives in lib/clip-engagement-api.ts.

export const COMMENT_MAX_CHARS = 500;
/** Seconds of actual playback before a clip view is recorded. */
export const VIEW_RECORD_AFTER_SEC = 2;

export interface ClipStats {
  clip_id: string;
  view_count: number;
  reaction_count: number;
  comment_count: number;
}

export const EMPTY_STATS = { view_count: 0, reaction_count: 0, comment_count: 0 } as const;

export interface ClipComment {
  id: string;
  clip_id: string;
  user_id: string;
  body: string;
  created_at: string;
  user: { gamertag: string; profile_image_url: string | null } | null;
}

type MaybeError = { code?: string | null; message?: string | null; status?: number } | null | undefined;

/**
 * True when an error means the engagement schema isn't deployed (the migration
 * hasn't been applied): unknown table/function in Postgres or PostgREST's
 * schema cache. The UI hides the feature instead of showing an error.
 */
export function isMissingSchemaError(error: MaybeError): boolean {
  if (!error) return false;
  const code = error.code ?? '';
  if (code === '42P01' || code === '42883' || code === 'PGRST202' || code === 'PGRST205') return true;
  if (error.status === 404) return true;
  const msg = (error.message ?? '').toLowerCase();
  return (
    msg.includes('could not find the table') ||
    msg.includes('could not find the function') ||
    (msg.includes('relation') && msg.includes('does not exist'))
  );
}

export type CommentErrorKind = 'rate_limited' | 'invalid' | 'unavailable' | 'failed';

export function classifyCommentError(error: MaybeError): CommentErrorKind {
  if (!error) return 'failed';
  const msg = error.message ?? '';
  if (msg.includes('clip_comment_rate_limited')) return 'rate_limited';
  if (error.code === '23514') return 'invalid';
  if (isMissingSchemaError(error)) return 'unavailable';
  return 'failed';
}

export const COMMENT_ERROR_COPY: Record<CommentErrorKind, string> = {
  rate_limited: "You're commenting too fast. Try again in a minute.",
  invalid: `Comments must be 1–${COMMENT_MAX_CHARS} characters.`,
  unavailable: 'Comments are not available right now.',
  failed: "Couldn't post your comment. Try again.",
};

/** Trimmed body, or null when it's empty or over the limit. */
export function normalizeCommentBody(raw: string): string | null {
  const body = raw.trim();
  if (body.length === 0 || body.length > COMMENT_MAX_CHARS) return null;
  return body;
}

/** Analytics bucket for a comment's length (never the text itself). */
export function commentLengthBucket(length: number): '1-20' | '21-100' | '101-250' | '251-500' {
  if (length <= 20) return '1-20';
  if (length <= 100) return '21-100';
  if (length <= 250) return '101-250';
  return '251-500';
}

/** Compact counts for the rail: 999, 1.2K, 12K, 1.5M. */
export function formatCount(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n < 1000) return String(Math.floor(n));
  const fmt = (v: number, unit: string) => {
    const s = v < 10 ? (Math.floor(v * 10) / 10).toFixed(1).replace(/\.0$/, '') : String(Math.floor(v));
    return s + unit;
  };
  if (n < 1_000_000) return fmt(n / 1000, 'K');
  return fmt(n / 1_000_000, 'M');
}

/** Optimistic reaction toggle: next reacted state and count. */
export function toggleReaction(state: { reacted: boolean; count: number }): { reacted: boolean; count: number } {
  return state.reacted
    ? { reacted: false, count: Math.max(0, state.count - 1) }
    : { reacted: true, count: state.count + 1 };
}

/** Adds a comment to a newest-first list, ignoring duplicates (realtime echo). */
export function mergeComment(list: ClipComment[], comment: ClipComment): ClipComment[] {
  if (list.some((c) => c.id === comment.id)) return list;
  return [comment, ...list].sort((a, b) => b.created_at.localeCompare(a.created_at));
}

/** Who may delete a comment: its author and the clip's owner. */
export function canDeleteComment(
  comment: Pick<ClipComment, 'user_id'>,
  viewerId: string | null | undefined,
  clipOwnerId: string,
): boolean {
  if (!viewerId) return false;
  return comment.user_id === viewerId || clipOwnerId === viewerId;
}
